import type { ToolkitCollection } from "../clients/arcade/arcade";

export const DEFAULT_TOOLKIT_NAMES = [
  "gmail",
  "github",
  "slack",
  "googlecalendar",
  "linkedin",
] as const;
export const MAX_DEFAULT_TOOLKITS = 6;

const GITHUB_DEFAULT_TOOL_NAMES = [
  "count_stargazers",
  "create_issue",
  "create_pull_request",
  "create_issue_comment",
  "create_reply_for_review_comment",
  "create_review_comment",
  "get_user_open_items",
  "get_review_workload",
  "search_my_repos",
  "get_repository",
  "get_file_contents",
  "list_issues",
  "get_issue",
  "list_projects",
  "list_pull_requests",
  "get_pull_request",
  "set_starred",
  "submit_pull_request_review",
  "who_am_i",
] as const;

function normalizeIdentifier(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, "");
}

function getToolFunctionName(fullyQualifiedName: string): string {
  const versionlessName =
    fullyQualifiedName.split("@")[0] ?? fullyQualifiedName;
  const segments = versionlessName.split(".");
  return segments.at(-1) ?? versionlessName;
}

function getDefaultToolNames(toolkitName: string): readonly string[] | null {
  const normalized = normalizeIdentifier(toolkitName);
  if (normalized.includes("github")) {
    return GITHUB_DEFAULT_TOOL_NAMES;
  }

  return null;
}

export function pickDefaultToolkitNames(
  availableToolkitNames: string[],
  preferredToolkitNames: readonly string[],
  maxDefaultToolkits = MAX_DEFAULT_TOOLKITS
): string[] {
  const availableByLowercase = new Map(
    availableToolkitNames.map((name) => [name.toLowerCase(), name])
  );
  const preferred = preferredToolkitNames
    .map((name) => availableByLowercase.get(name.toLowerCase()))
    .filter((name): name is string => Boolean(name));

  if (preferred.length > 0) {
    return preferred.slice(0, maxDefaultToolkits);
  }

  return availableToolkitNames.slice(0, maxDefaultToolkits);
}

function pickDefaultToolNamesForToolkit(
  toolkitCollection: ToolkitCollection,
  toolkitName: string
): string[] {
  const toolkit = toolkitCollection.toolkitMap?.get(toolkitName);
  if (!toolkit) {
    return [];
  }

  const preferredToolNames = getDefaultToolNames(toolkitName);
  if (!preferredToolNames) {
    return toolkit.tools.map((tool) => tool.fully_qualified_name);
  }

  const preferredToolSet = new Set(
    preferredToolNames.map((toolName) => normalizeIdentifier(toolName))
  );
  const preferredTools = toolkit.tools.filter((tool) =>
    preferredToolSet.has(
      normalizeIdentifier(getToolFunctionName(tool.fully_qualified_name))
    )
  );
  const toolsToSelect =
    preferredTools.length > 0 ? preferredTools : toolkit.tools;

  return toolsToSelect.map((tool) => tool.fully_qualified_name);
}

export function buildDefaultToolkitSelection(
  toolkitCollection: ToolkitCollection,
  preferredToolkitNames: readonly string[] = DEFAULT_TOOLKIT_NAMES,
  maxDefaultToolkits = MAX_DEFAULT_TOOLKITS
): {
  selectedToolkits: string[];
  selectedTools: string[];
} {
  const availableToolkitNames = toolkitCollection.toolkitMap
    ? Array.from(toolkitCollection.toolkitMap.keys())
    : [];
  const selectedToolkitNames = pickDefaultToolkitNames(
    availableToolkitNames,
    preferredToolkitNames,
    maxDefaultToolkits
  );

  const selectedToolNames = Array.from(
    new Set(
      selectedToolkitNames.flatMap((toolkitName) =>
        pickDefaultToolNamesForToolkit(toolkitCollection, toolkitName)
      )
    )
  );

  return {
    selectedToolkits: selectedToolkitNames,
    selectedTools: selectedToolNames,
  };
}
